import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { PageHeader } from '../../components/layout/AppShell'
import { Button } from '../../components/ui/button'
import { ErrorNote } from '../../components/ui/callout'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import {
  Checkbox,
  Input,
  Label,
  Select,
  Skeleton,
  Textarea,
} from '../../components/ui/primitives'
import { formatDate } from '../../lib/ui'
import { type AgentArticle, type CabMeeting, agentApi } from '../api'
import { RiskPill, StatePill } from '../components/ChangeBits'

/** CAB agenda view — SPEC section 9. */
export function Cab() {
  const { data, isLoading } = useQuery({
    queryKey: ['agent-cab'],
    queryFn: () => agentApi.get<CabMeeting[]>('/cab/'),
  })

  return (
    <div>
      <PageHeader
        title="CAB"
        description="Change advisory board agendas. A normal change cannot be approved until it appears on one."
      />

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">No CAB meetings scheduled.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data?.map((meeting) => (
            <Card key={meeting.id}>
              <CardHeader>
                <CardTitle>{formatDate(meeting.scheduled_at)}</CardTitle>
                <span className="shrink-0 text-xs text-muted-foreground">
                  Chair: {meeting.chair_name ?? 'unassigned'}
                </span>
              </CardHeader>
              <CardContent>
                {meeting.agenda_items.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing on the agenda.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-border">
                        {meeting.agenda_items.map((item) => (
                          <tr key={item.id}>
                            <td className="whitespace-nowrap py-2 pr-3">
                              <Link
                                to={`/agent/changes/${item.ref}`}
                                className="font-mono text-xs text-primary hover:underline"
                              >
                                {item.ref}
                              </Link>
                            </td>
                            <td className="py-2 pr-3 font-medium">{item.title}</td>
                            <td className="whitespace-nowrap py-2 pr-3 text-xs capitalize text-muted-foreground">
                              {item.change_class}
                            </td>
                            <td className="py-2 pr-3">
                              <RiskPill value={item.risk} />
                            </td>
                            <td className="py-2">
                              <StatePill value={item.outcome} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {meeting.minutes && (
                  <p className="mt-3 whitespace-pre-wrap border-t border-border pt-2.5 text-sm text-muted-foreground">
                    {meeting.minutes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

/** KB editor — SPEC section 9. */
export function KbEditor() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Partial<AgentArticle> | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['agent-kb'],
    queryFn: () => agentApi.get<AgentArticle[]>('/kb/'),
  })

  const save = useMutation({
    mutationFn: (article: Partial<AgentArticle>) =>
      article.id
        ? agentApi.patch(`/kb/${article.slug}/`, article)
        : agentApi.post('/kb/', article),
    onSuccess: () => {
      setEditing(null)
      void queryClient.invalidateQueries({ queryKey: ['agent-kb'] })
    },
  })

  return (
    <div>
      <PageHeader
        title="Knowledge base"
        description="Articles published to the portal. HTML is sanitised on save."
        actions={
          <Button
            size="sm"
            onClick={() =>
              setEditing({
                slug: '',
                title: '',
                body_md: '',
                org_visibility: 'all',
                published: false,
              })
            }
          >
            <Plus />
            New article
          </Button>
        }
      />

      <div className="space-y-4">
        {editing && (
          <Card>
            <CardHeader>
              <CardTitle>{editing.id ? 'Edit article' : 'New article'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="kb-title">Title</Label>
                <Input
                  id="kb-title"
                  placeholder="How to request a new laptop"
                  value={editing.title ?? ''}
                  onChange={(event) => setEditing({ ...editing, title: event.target.value })}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="kb-slug">Slug</Label>
                <Input
                  id="kb-slug"
                  placeholder="slug-in-the-url"
                  value={editing.slug ?? ''}
                  disabled={Boolean(editing.id)}
                  onChange={(event) => setEditing({ ...editing, slug: event.target.value })}
                />
                {Boolean(editing.id) && (
                  <p className="text-xs text-muted-foreground">
                    The slug is the article's URL and cannot be changed once published.
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="kb-body">Body</Label>
                <Textarea
                  id="kb-body"
                  rows={12}
                  placeholder="Body. HTML is sanitised on save."
                  value={editing.body_md ?? ''}
                  onChange={(event) => setEditing({ ...editing, body_md: event.target.value })}
                  className="font-mono"
                />
              </div>

              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="kb-visibility">Visibility</Label>
                  <Select
                    id="kb-visibility"
                    value={editing.org_visibility ?? 'all'}
                    onChange={(event) =>
                      setEditing({ ...editing, org_visibility: event.target.value })
                    }
                    className="w-auto"
                  >
                    <option value="all">All organisations</option>
                    <option value="internal">Internal only</option>
                  </Select>
                </div>

                <label className="flex h-8 items-center gap-2 text-sm">
                  <Checkbox
                    checked={Boolean(editing.published)}
                    onChange={(event) => setEditing({ ...editing, published: event.target.checked })}
                  />
                  Published
                </label>

                <div className="ml-auto flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={!editing.title || !editing.slug || save.isPending}
                    onClick={() => save.mutate(editing)}
                  >
                    {save.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>

              {save.error && <ErrorNote message={(save.error as Error).message} />}
            </CardContent>
          </Card>
        )}

        <Card>
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 4 }, (_, row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </div>
          ) : (data?.length ?? 0) === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-muted-foreground">
              No articles yet.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {data?.map((article) => (
                <li
                  key={article.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{article.title}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      /{article.slug} · {article.org_visibility} ·{' '}
                      {article.published ? 'published' : 'draft'} · {formatDate(article.updated_at)}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => setEditing(article)}
                  >
                    Edit
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  )
}
